// Skeleton placeholders for Ultimate-mode routes — matches each page's layout
// so route lazy-load + initial data fetch shows YouTube-style shimmer instead
// of an empty white screen.

import { ReactNode } from "react";

function Pulse({ className = "" }: { className?: string }) {
  return (
    <div
      className={`bg-slate-200 dark:bg-[#2B2726] animate-pulse rounded-md ${className}`}
    />
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div
      className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]"
      style={{
        paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 5.5rem)",
      }}
    >
      <div className="px-4">{children}</div>
    </div>
  );
}

function Heading() {
  return (
    <div className="flex items-start justify-between mb-6">
      <div className="space-y-2">
        <Pulse className="h-7 w-32" />
        <Pulse className="h-7 w-44" />
      </div>
      <Pulse className="h-7 w-20 rounded-full" />
    </div>
  );
}

export function UltimateDashboardSkeleton() {
  return (
    <Shell>
      <Heading />
      {/* Period selector */}
      <Pulse className="h-11 w-full rounded-2xl mb-5" />
      {/* Hero KPI */}
      <div className="rounded-2xl border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 p-5 mb-4 space-y-3">
        <Pulse className="h-3 w-24" />
        <Pulse className="h-12 w-40" />
        <div className="flex gap-2">
          <Pulse className="h-6 w-20 rounded-full" />
          <Pulse className="h-6 w-24 rounded-full" />
        </div>
      </div>
      {/* 2-col grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 p-5"
          >
            <Pulse className="h-3 w-16 mb-2" />
            <Pulse className="h-7 w-20" />
          </div>
        ))}
      </div>
      {/* Top products card */}
      <div className="rounded-2xl border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 p-5">
        <Pulse className="h-3 w-32 mb-3" />
        <Pulse className="h-40 w-full" />
      </div>
    </Shell>
  );
}

export function UltimateTransactionsSkeleton() {
  return (
    <Shell>
      <Heading />
      <Pulse className="h-12 w-full rounded-2xl mb-3" />
      <Pulse className="h-10 w-full rounded-xl mb-4" />
      <div className="flex gap-2 mb-4">
        <Pulse className="h-9 flex-1 rounded-xl" />
        <Pulse className="h-9 flex-1 rounded-xl" />
        <Pulse className="h-9 flex-1 rounded-xl" />
      </div>
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <Pulse className="h-4 w-32" />
              <Pulse className="h-5 w-20" />
            </div>
            <Pulse className="h-3 w-48" />
          </div>
        ))}
      </div>
    </Shell>
  );
}

export function UltimateProductsSkeleton() {
  return (
    <Shell>
      <Heading />
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-2xl border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 p-5 space-y-2">
          <Pulse className="h-3 w-12" />
          <Pulse className="h-7 w-16" />
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 p-5 space-y-2">
          <Pulse className="h-3 w-20" />
          <Pulse className="h-7 w-24" />
        </div>
      </div>
      <Pulse className="h-10 w-full rounded-xl mb-3" />
      <div className="flex gap-2 mb-4">
        <Pulse className="h-9 w-16 rounded-xl" />
        <Pulse className="h-9 w-24 rounded-xl" />
        <Pulse className="h-9 w-16 rounded-xl" />
        <Pulse className="h-9 w-20 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 p-3 space-y-2"
          >
            <Pulse className="aspect-square w-full rounded-xl" />
            <Pulse className="h-3 w-3/4" />
            <Pulse className="h-4 w-16" />
            <Pulse className="h-4 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </Shell>
  );
}

export function UltimateSettingsSkeleton() {
  return (
    <Shell>
      <Heading />
      <div className="rounded-2xl border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 p-5 mb-4 flex items-center gap-3">
        <Pulse className="h-12 w-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <Pulse className="h-4 w-32" />
          <Pulse className="h-3 w-44" />
        </div>
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="mb-4">
          <Pulse className="h-3 w-20 mb-2" />
          <div className="rounded-2xl border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 overflow-hidden">
            {Array.from({ length: 2 }).map((_, j) => (
              <div key={j} className="flex items-center gap-3 p-4 border-b border-slate-100 dark:border-[#2B2726] last:border-0">
                <Pulse className="h-8 w-8 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Pulse className="h-4 w-32" />
                  <Pulse className="h-3 w-44" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </Shell>
  );
}

export function UltimateGenericSkeleton() {
  return (
    <Shell>
      <Heading />
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Pulse key={i} className="h-16 w-full rounded-2xl" />
        ))}
      </div>
    </Shell>
  );
}
